import React, { useState, useEffect, useCallback } from 'react';
import './GovernanceReal.css';

const RPC_URL = 'http://localhost:9545';
const ADMIN_ADDRESS = '0x372d3c99b7bdb6dec219daf4aef96bc2062e6090';

const GovernanceReal = () => {
  const [activeTab, setActiveTab] = useState('proposals');
  const [proposals, setProposals] = useState([]);
  const [treasury, setTreasury] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [governanceStats, setGovernanceStats] = useState(null);
  const [userAddress, setUserAddress] = useState(null);

  const rpcCall = useCallback(async (method, params = []) => {
    const response = await fetch(RPC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method,
        params
      })
    });
    return response.json();
  }, []);

  const fetchGovernanceStats = useCallback(async () => {
    try {
      const response = await rpcCall('avo_getGovernanceStats');
      setGovernanceStats(response.result);
    } catch (error) {
      console.error('Error fetching governance stats:', error);
    }
  }, [rpcCall]);

  const fetchProposals = useCallback(async () => {
    try {
      console.log('🔍 [Explorer] Fetching proposals from AVO RPC...');
      const listResponse = await rpcCall('avo_listProposals');
      const proposalsList = listResponse.result || [];
      
      console.log(`📋 [Explorer] Found ${proposalsList.length} proposal(s)`);
      
      if (proposalsList.length === 0) {
        setProposals([]);
        return;
      }
      
      // Fetch detailed info for each proposal
      const detailedProposals = await Promise.all(
        proposalsList.map(async (prop) => {
          const detailResponse = await rpcCall('avo_getProposal', [{ proposal_id: prop.id }]);
          const details = detailResponse.result;
          
          return {
            id: details.id,
            title: details.title,
            description: details.description,
            proposer: details.proposer,
            status: details.status || 'Active',
            type: details.proposal_type || 'Custom',
            parameter: details.parameter,
            currentValue: details.current_value,
            newValue: details.new_value,
            createdAt: details.created_at,
            votingEndsAt: details.voting_ends_at,
            voteCounts: {
              for: parseInt(details.vote_counts?.for || '0x0', 16) / 1e18,
              against: parseInt(details.vote_counts?.against || '0x0', 16) / 1e18,
              abstain: parseInt(details.vote_counts?.abstain || '0x0', 16) / 1e18
            }
          };
        })
      );
      
      console.log('✅ [Explorer] Loaded proposals:', detailedProposals);
      setProposals(detailedProposals);
    } catch (error) {
      console.error('❌ [Explorer] Error fetching proposals:', error);
      setProposals([]);
    }
  }, [rpcCall]);

  const fetchTreasuryData = useCallback(async () => {
    try {
      const [accountsRes, balancesRes] = await Promise.all([
        rpcCall('avo_getTreasuryAccounts'),
        rpcCall('avo_getTreasuryBalances')
      ]);

      const treasuryData = {
        accounts: accountsRes.result,
        balances: balancesRes.result.balances,
        feeDistribution: balancesRes.result.fee_distribution
      };

      setTreasury(treasuryData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching treasury data:', error);
      setLoading(false);
    }
  }, [rpcCall]);

  // Fetch Governance Stats, Proposals, and Treasury Data
  useEffect(() => {
    fetchGovernanceStats();
    fetchProposals();
    fetchTreasuryData();
    
    const interval = setInterval(() => {
      fetchGovernanceStats();
      fetchProposals();
      fetchTreasuryData();
    }, 30000); // Update every 30s
    
    return () => clearInterval(interval);
  }, [fetchGovernanceStats, fetchProposals, fetchTreasuryData]);

  // Get user address from wallet
  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.request({ method: 'eth_accounts' })
        .then(accounts => {
          if (accounts.length > 0) {
            setUserAddress(accounts[0]);
          }
        });
    }
  }, []);

  const formatBalance = (balance) => {
    if (!balance) return '0';
    const num = parseInt(balance, 16) / 1e18;
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getPhaseInfo = () => {
    if (!governanceStats || !governanceStats.phase) return null;
    
    const phase = governanceStats.phase;
    
    if (phase.AdminControlled) {
      return {
        type: 'AdminControlled',
        label: '⚡ Admin Bootstrap',
        color: '#ff9500',
        description: 'Solo admin puede crear propuestas',
        data: phase.AdminControlled
      };
    } else if (phase.Transition) {
      return {
        type: 'Transition',
        label: '🔄 Transición',
        color: '#ffcc00',
        description: 'Admin + Whitelist pueden proponer',
        data: phase.Transition
      };
    } else if (phase.Decentralized) {
      return {
        type: 'Decentralized',
        label: '🌐 Descentralizado',
        color: '#00ff00',
        description: 'Cualquiera con 1 AVO puede proponer',
        data: phase.Decentralized
      };
    }
    return null;
  };

  const canCreateProposal = () => {
    if (!userAddress || !governanceStats) return false;
    
    const phaseInfo = getPhaseInfo();
    if (!phaseInfo) return false;
    
    if (phaseInfo.type === 'AdminControlled') {
      return userAddress.toLowerCase() === ADMIN_ADDRESS.toLowerCase();
    }
    // En otras fases, permitir (verificación real en backend)
    return true;
  };

  const renderProposalsTab = () => {
    const phaseInfo = getPhaseInfo();
    const canCreate = canCreateProposal();
    
    return (
      <div className="proposals-section">
        {/* Phase Indicator */}
        {phaseInfo && (
          <div className="phase-indicator" style={{ borderColor: phaseInfo.color }}>
            <div className="phase-header">
              <span className="phase-label" style={{ color: phaseInfo.color }}>
                {phaseInfo.label}
              </span>
              <span className="phase-description">{phaseInfo.description}</span>
            </div>
            
            {phaseInfo.type === 'AdminControlled' && phaseInfo.data && (
              <div className="phase-progress">
                <p className="progress-label">Progreso hacia Transición:</p>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${Math.min((phaseInfo.data.current_holders || 0) / phaseInfo.data.min_community_size * 100, 100)}%`,
                      background: phaseInfo.color 
                    }}
                  />
                </div>
                <p className="progress-text">
                  {phaseInfo.data.current_holders || 0} / {phaseInfo.data.min_community_size} holders
                </p>
              </div>
            )}
          </div>
        )}

        {/* Vote Fee Warning */}
        {governanceStats && (
          <div className="vote-fee-warning">
            <h4>🔥 Sistema Anti-Spam</h4>
            <div className="fee-info">
              <div className="fee-item">
                <span className="fee-label">Fee por voto:</span>
                <span className="fee-value">
                  {(parseInt(governanceStats.vote_fee) / 1e18).toFixed(1)} AVO (quemado)
                </span>
              </div>
              <div className="fee-item">
                <span className="fee-label">Total quemado:</span>
                <span className="fee-value">
                  {(parseInt(governanceStats.total_fees_burned) / 1e18).toLocaleString()} AVO
                </span>
              </div>
              <div className="fee-item">
                <span className="fee-label">Votos totales:</span>
                <span className="fee-value">
                  {governanceStats.total_votes_cast.toLocaleString()}
                </span>
              </div>
            </div>
            <p className="fee-explanation">
              Este fee previene spam de votaciones y reduce el supply total.
              Tu poder de voto NO se ve afectado por el fee.
            </p>
          </div>
        )}

        <div className="section-header">
          <h3>Active Proposals</h3>
          <div className="create-btn-container">
            <button 
              className="create-btn" 
              onClick={() => setShowCreateModal(true)}
              disabled={!canCreate}
            >
              Create Proposal
            </button>
            {!canCreate && phaseInfo && phaseInfo.type === 'AdminControlled' && (
              <span className="btn-tooltip">
                Solo admin puede crear propuestas en fase de bootstrap
              </span>
            )}
          </div>
        </div>

      <div className="proposals-list">
        {proposals.length === 0 ? (
          <div className="empty-state">
            <p>No active proposals</p>
            {canCreate && (
              <button className="create-btn" onClick={() => setShowCreateModal(true)}>
                Create First Proposal
              </button>
            )}
          </div>
        ) : (
          proposals.map(proposal => (
            <div key={proposal.id} className="proposal-card">
              <div className="proposal-header">
                <span className="proposal-id">#{proposal.id}</span>
                <span className={`proposal-status status-${proposal.status.toLowerCase()}`}>
                  {proposal.status.toUpperCase()}
                </span>
              </div>
              <h4 className="proposal-title">{proposal.title}</h4>
              <p className="proposal-description">{proposal.description}</p>
              <div className="proposal-meta">
                <span>Proposer: {shortenAddress(proposal.proposer)}</span>
                <span>Type: {proposal.type.replace('_', ' ')}</span>
                {proposal.parameter && (
                  <span>Parameter: {proposal.parameter}</span>
                )}
                {proposal.currentValue && proposal.newValue && (
                  <span>Change: {proposal.currentValue} → {proposal.newValue}</span>
                )}
              </div>
              {proposal.status.toLowerCase() === 'active' && (
                <div className="voting-bar">
                  <div className="vote-stats">
                    <span>For: {proposal.voteCounts.for.toFixed(2)} AVO</span>
                    <span>Against: {proposal.voteCounts.against.toFixed(2)} AVO</span>
                    <span>Abstain: {proposal.voteCounts.abstain.toFixed(2)} AVO</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-for" 
                      style={{ 
                        width: `${
                          (proposal.voteCounts.for / 
                          Math.max(proposal.voteCounts.for + proposal.voteCounts.against + proposal.voteCounts.abstain, 1)) 
                          * 100
                        }%` 
                      }}
                    />
                  </div>
                  <div className="proposal-footer">
                    <span>Created: {new Date(proposal.createdAt * 1000).toLocaleDateString()}</span>
                    <span>Ends: {new Date(proposal.votingEndsAt * 1000).toLocaleDateString()}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
  };

  const renderTreasuryTab = () => {
    if (!treasury) {
      return <div className="loading">Loading treasury data...</div>;
    }

    const accounts = treasury.accounts;
    
    // Calculate total balance from actual account balances (REAL DATA from RocksDB)
    const totalBalance = Object.values(accounts).reduce((sum, account) => {
      const balance = parseInt(account.balance, 16) / 1e18;
      return sum + balance;
    }, 0);

    return (
      <div className="treasury-section">
        <div className="treasury-overview">
          <h3>Treasury Overview</h3>
          <div className="total-balance">
            <span className="label">Total Treasury</span>
            <span className="value">{totalBalance.toLocaleString()} AVO</span>
          </div>
        </div>

        <div className="treasury-accounts">
          <h3>Treasury Accounts</h3>
          <div className="accounts-grid">
            {Object.entries(accounts).map(([key, account]) => (
              <div key={key} className="account-card">
                <div className="account-header">
                  <span className="account-name">{key.replace('_', ' ')}</span>
                  {account.multisig_threshold && (
                    <span className="multisig-badge">{account.multisig_threshold}</span>
                  )}
                </div>
                <div className="account-address">{shortenAddress(account.address)}</div>
                <div className="account-balance">
                  {formatBalance(account.balance)} AVO
                </div>
                <div className="account-description">{account.description}</div>
                {account.vesting_lock_until && (
                  <div className="vesting-info">
                    Locked until: {new Date(account.vesting_lock_until).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="fee-distribution">
          <h3>Fee Distribution</h3>
          <div className="distribution-grid">
            {treasury.feeDistribution && Object.entries(treasury.feeDistribution).map(([key, value]) => (
              <div key={key} className="distribution-item">
                <span className="dist-label">{key.replace('_', ' ')}</span>
                <span className="dist-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderVotingTab = () => (
    <div className="voting-section">
      <div className="voting-info">
        <h3>Voting Power</h3>
        <div className="voting-stats">
          <div className="stat-card">
            <span className="stat-label">Your Voting Power</span>
            <span className="stat-value">0 AVO</span>
            <span className="stat-hint">Connect wallet to see voting power</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Delegated Power</span>
            <span className="stat-value">0 AVO</span>
            <span className="stat-hint">Voting power you've delegated</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Received Delegations</span>
            <span className="stat-value">0 AVO</span>
            <span className="stat-hint">Power delegated to you</span>
          </div>
        </div>

        <div className="delegation-controls">
          <h4>Delegate Voting Power</h4>
          <div className="delegate-form">
            <input 
              type="text" 
              placeholder="Delegate Address (0x...)" 
              className="delegate-input"
            />
            <select className="delegate-type">
              <option value="full">Full Delegation</option>
              <option value="partial">Partial Delegation</option>
              <option value="categorical">Category-Specific</option>
              <option value="temporary">Temporary</option>
            </select>
            <button className="delegate-btn">Delegate</button>
          </div>
        </div>
      </div>

      <div className="voting-history">
        <h3>Your Voting History</h3>
        <div className="empty-state">
          <p>No voting history available</p>
          <p className="hint">Connect wallet to view your votes</p>
        </div>
      </div>
    </div>
  );

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Create Proposal</h3>
            <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
          </div>

          <form className="create-proposal-form">
            <div className="form-group">
              <label>Proposal Type</label>
              <select className="form-input">
                <option value="parameter_change">Parameter Change</option>
                <option value="treasury_spend">Treasury Spend</option>
                <option value="network_upgrade">Network Upgrade</option>
                <option value="validator_change">Validator Change</option>
                <option value="emergency">Emergency Action</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="form-group">
              <label>Title</label>
              <input type="text" className="form-input" placeholder="Proposal title" />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea className="form-textarea" rows="4" placeholder="Detailed description" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Voting Period (days)</label>
                <input type="number" className="form-input" defaultValue="7" min="1" max="30" />
              </div>
              <div className="form-group">
                <label>Execution Delay (days)</label>
                <input type="number" className="form-input" defaultValue="2" min="0" max="14" />
              </div>
            </div>

            <div className="form-group">
              <label>Quorum Required (%)</label>
              <input type="number" className="form-input" defaultValue="10" min="1" max="100" />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-submit">
                Create Proposal
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="governance-real">
        <div className="loading-state">Loading governance data...</div>
      </div>
    );
  }

  return (
    <div className="governance-real">
      <div className="governance-header">
        <h2>Governance</h2>
        <div className="governance-tabs">
          <button 
            className={`tab ${activeTab === 'proposals' ? 'active' : ''}`}
            onClick={() => setActiveTab('proposals')}
          >
            Proposals
          </button>
          <button 
            className={`tab ${activeTab === 'treasury' ? 'active' : ''}`}
            onClick={() => setActiveTab('treasury')}
          >
            Treasury
          </button>
          <button 
            className={`tab ${activeTab === 'voting' ? 'active' : ''}`}
            onClick={() => setActiveTab('voting')}
          >
            Voting
          </button>
        </div>
      </div>

      <div className="governance-content">
        {activeTab === 'proposals' && renderProposalsTab()}
        {activeTab === 'treasury' && renderTreasuryTab()}
        {activeTab === 'voting' && renderVotingTab()}
      </div>

      {renderCreateModal()}
    </div>
  );
};

export default GovernanceReal;